<script setup lang="ts">
import { ref, defineProps, defineEmits, computed, watch, onMounted, onUnmounted } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";

interface Props {
  show: boolean;
  dialogObj: {
    id: number;
    title: string;
    content: string;
    image_url: string;
    hyperlink: string;
    show_type: number;
    weight: number;
    created_at: string;
    confirm_text: string;
  };
  items?: Array<{
    id?: number;
    title?: string;
    content?: string;
    image_url: string;
    hyperlink?: string;
  }>;
}

const props = defineProps<Props>();
const emit = defineEmits(["update:show", "confirm"]);

const redeemCode = ref("");
const isLoading = ref(false);
const modules: any[] = [];
const slides = computed(() => {
  if (Array.isArray(props.items) && props.items.length) return props.items;
  return props.dialogObj ? [props.dialogObj] : [];
});
const currentIndex = ref(0);
const autoForward = ref(true);
const autoplayInterval = 3000;
let autoTimer: any = null;
const currentTitle = computed(() => {
  const s = slides.value;
  const t = s?.[currentIndex.value]?.title || props.dialogObj?.title || "";
  return t;
});
const swiperIns = ref<any>(null);
const onSwiper = (swiper: any) => {
  swiperIns.value = swiper;
  if (props.show) startAuto();
};
const onSlideChange = (swiper: any) => {
  currentIndex.value = swiper?.activeIndex || 0;
};
const goPrev = () => {
  if (!swiperIns.value) return;
  if (currentIndex.value <= 0) return;
  swiperIns.value.slidePrev();
};
const goNext = () => {
  if (!swiperIns.value) return;
  if (currentIndex.value >= slides.value.length - 1) return;
  swiperIns.value.slideNext();
};
const isPrevDisabled = computed(() => currentIndex.value <= 0);
const isNextDisabled = computed(() => currentIndex.value >= slides.value.length - 1);
const startAuto = () => {
  stopAuto();
  if (!swiperIns.value || slides.value.length <= 1) return;
  autoTimer = setInterval(() => {
    if (autoForward.value) {
      if (currentIndex.value >= slides.value.length - 1) {
        autoForward.value = false;
        swiperIns.value.slidePrev();
      } else {
        swiperIns.value.slideNext();
      }
    } else {
      if (currentIndex.value <= 0) {
        autoForward.value = true;
        swiperIns.value.slideNext();
      } else {
        swiperIns.value.slidePrev();
      }
    }
  }, autoplayInterval);
};
const stopAuto = () => {
  if (autoTimer) {
    clearInterval(autoTimer);
    autoTimer = null;
  }
};
watch(() => props.show, (val) => {
  if (val) startAuto();
  else stopAuto();
});
onMounted(() => {
  if (props.show && swiperIns.value) startAuto();
});
onUnmounted(() => {
  stopAuto();
});

const handleClose = () => {
  emit("update:show", false);
  redeemCode.value = "";
};

const handlePular = async (item?: any) => {
  
  try {
    const link = item?.hyperlink || props.dialogObj?.hyperlink;
    if (link) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        setTimeout(() => {
          window.location.href = link;
        }, 100);
      } else {
        window.open(link, "_self");
      }

    } else {
      emit("confirm");
    }


  } catch (error: any) {
  } finally {
    isLoading.value = false;
  }
};
</script>

<template> 
  <v-dialog :model-value="show" @update:model-value="emit('update:show', $event)" width="92%" @click:outside="handleClose" class="bonus-dialog">
    <v-card class="bonus-card">
      <div class="dialog-content">
        <div class="dialog-top">
          <div class="dialog-top-title">{{ currentTitle }}</div>
          <div class="dialog-top-nav">
            <div class="top-nav-btn" :class="{ disabled: isPrevDisabled }" @click="goPrev">
              <v-icon size="34">mdi-chevron-left</v-icon>
            </div>
            <div class="top-nav-btn" :class="{ disabled: isNextDisabled }" @click="goNext">
              <v-icon size="34">mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>
        <Swiper
          :modules="modules"
          :slides-per-view="1"
          :space-between="12"
          :loop="false"
          @swiper="onSwiper"
          @slideChange="onSlideChange"
          class="dialog-swiper"
        >
          <SwiperSlide v-for="(item, idx) in slides" :key="idx">
            <div class="slide-wrap" @click="handlePular(item)">
              <v-img aspect-ratio="16/9" cover :src="item.image_url"></v-img>
            </div>
          </SwiperSlide>
        </Swiper>
        <div class="dialog-close" @click="handleClose">
          <v-icon size="20">mdi-close</v-icon>
        </div>
      </div>
      </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.bonus-dialog {
  :deep(.v-overlay__content) {
    align-items: center;
    border-radius: 8px;
    max-width:552px !important ;
    margin: 0 !important;
    padding: 0 !important;
  }
}

.bonus-card {
  width: 100%;
  background: transparent;
  border-radius: 8px;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dialog-header {
  text-align: center;
}

.dialog-title {
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 8px;
}

.dialog-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.dialog-swiper {
  max-width: 100%;
  width: 100%;
  background: #0f1923;
  padding: 0 8px 8px 8px;
  border-radius: 0 0 8px 8px;
}
.dialog-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
  width: 100%;
  height: 34px;
  background: #0f1923;
  padding: 0 8px;
  border-radius: 8px 8px 0 0;
}
.dialog-top-title {
    width: 240px;
    font-size: 18px;
    font-weight: 600;
    padding: 8px 0;
    color: #fff;
    text-align: start;
    text-transform: uppercase;
}
.dialog-top-nav {
  display: flex;
  gap: 6px;
}
.top-nav-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8c9099;
  cursor: pointer;
}
.top-nav-btn.disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: none;
}
.slide-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.slide-title {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 8px;
  background: rgba(0,0,0,0.2);
  border-radius: 6px;
}
.dialog-close {
  width: 34px;
  height: 34px;
  margin: 16px auto 0;
  border-radius: 50%;
  display: flex;
  border: 2px solid #fff;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  bottom: 0;
  font-weight: 600;
}

.redeem-input {
  :deep(.v-field) {
    height: 46px;
    line-height: 46px;
    border-radius: 8px;
    background: #2b324d;

    .v-field__input {
      color: #fff;
      font-size: 16px;
      padding: 0 16px;
      height: 46px;
      line-height: 46px;
    }

    .v-field__outline {
      display: none;
    }
  }
}

.dialog-footer {
  margin-top: 8px;
}

.redeem-btn {
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.5px;
  background: linear-gradient(0deg, #c9b737, #2abb27);
  border-radius: 22px;
}
</style>
